import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useT } from '../i18n';
import { weekdayInitials } from '../lib/format';

/**
 * Themed date inputs.
 *
 * The native <input type="date"> popup is browser chrome — CSS can't reach it,
 * so it always looked like a stranger inside the app. These render the calendar
 * ourselves instead.
 *
 * The value contract is deliberately identical to the native inputs they
 * replace, so every caller's parsing and validation is untouched:
 *   DateField      -> "YYYY-MM-DD"        (as <input type="date">)
 *   DateTimeField  -> "YYYY-MM-DDTHH:mm"  (as <input type="datetime-local">)
 */

/**
 * This grid starts on Sunday, unlike the manager calendar's Monday-first one,
 * because it is laid out from `new Date(y, m, 1).getDay()`. The shared helper
 * hands back a Monday-first week, so the last day is rotated to the front.
 */
const sundayFirst = (): string[] => {
  const week = weekdayInitials();
  return [week[6], ...week.slice(0, 6)];
};

const pad = (n: number) => String(n).padStart(2, '0');
const iso = (y: number, m: number, d: number) => `${y}-${pad(m + 1)}-${pad(d)}`;

/** Local today, not UTC — toISOString() would roll the date over early in +hh offsets. */
const localToday = (): string => {
  const d = new Date();
  return iso(d.getFullYear(), d.getMonth(), d.getDate());
};

/** Parse "YYYY-MM-DD" as a local date. new Date(str) would read it as UTC. */
const parse = (s: string) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const pretty = (s: string, locale: string) =>
  parse(s).toLocaleDateString(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

/** "14:00" -> "02:00 PM". 12-hour everywhere, as the rest of the app does. */
const prettyTime = (hhmm: string, locale: string) => {
  const [h, m] = hhmm.split(':').map(Number);
  return new Date(2000, 0, 1, h, m).toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const monthLabel = (y: number, m: number, locale: string) =>
  new Date(y, m, 1).toLocaleDateString(locale, { month: 'long', year: 'numeric' });

/** "AM" / "PM" in the current language, for the meridiem wheel. */
const meridiem = (pm: boolean, locale: string) =>
  new Date(2000, 0, 1, pm ? 13 : 1)
    .toLocaleTimeString(locale, { hour: 'numeric', hour12: true })
    .replace(/[\d०-९\s]/g, '');

/* ------------------------------- calendar -------------------------------- */

function Calendar({
  value,
  min,
  onPick,
}: {
  value: string;
  min?: string;
  onPick: (v: string) => void;
}) {
  const { t, locale } = useT();
  const anchor = value || min || localToday();
  const [year, setYear] = useState(Number(anchor.slice(0, 4)));
  const [month, setMonth] = useState(Number(anchor.slice(5, 7)) - 1);

  const today = localToday();
  const firstWeekday = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells: (number | null)[] = [
    ...Array.from({ length: firstWeekday }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  const step = (delta: number) => {
    const d = new Date(year, month + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth());
  };

  // "YYYY-MM-DD" strings sort lexicographically, so a plain compare is enough.
  const blocked = (day: number) => (min ? iso(year, month, day) < min : false);

  return (
    <div className="cal">
      <div className="cal-head">
        <button type="button" className="cal-nav" onClick={() => step(-1)} aria-label={t('calPrevMonth')}>
          <i className="ri-arrow-left-s-line" />
        </button>
        <b>{monthLabel(year, month, locale)}</b>
        <button type="button" className="cal-nav" onClick={() => step(1)} aria-label={t('calNextMonth')}>
          <i className="ri-arrow-right-s-line" />
        </button>
      </div>

      <div className="cal-grid cal-week">
        {sundayFirst().map((w, i) => (
          <span key={i}>{w}</span>
        ))}
      </div>

      <div className="cal-grid">
        {cells.map((day, i) =>
          day === null ? (
            <span key={`x${i}`} />
          ) : (
            <button
              type="button"
              key={day}
              className={`cal-day${iso(year, month, day) === value ? ' sel' : ''}${
                iso(year, month, day) === today ? ' today' : ''
              }`}
              disabled={blocked(day)}
              aria-current={iso(year, month, day) === today ? 'date' : undefined}
              onClick={() => onPick(iso(year, month, day))}
            >
              {day}
            </button>
          ),
        )}
      </div>

      {/* "Today" only when it is a real choice: not when today is already the
          picked date, and not when today cannot be picked at all. */}
      {value !== today && !(min && today < min) && (
        <div className="cal-foot">
          <button type="button" className="btn ghost sm" onClick={() => onPick(today)}>
            {t('today')}
          </button>
        </div>
      )}
    </div>
  );
}

/* ------------------------------ popover shell ---------------------------- */

function Popover({
  value,
  min,
  label,
  placeholder,
  onPick,
}: {
  value: string;
  min?: string;
  label: string;
  placeholder: string;
  onPick: (v: string) => void;
}) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const pop = useRef<HTMLDivElement>(null);

  // The calendar is wider than many of the fields it hangs from, so on a phone
  // it can run off either edge. Slide it back inside the screen before it paints.
  // `translate` (not `transform`), which the popIn animation would overwrite.
  useLayoutEffect(() => {
    const el = pop.current;
    if (!open || !el || !wrap.current || getComputedStyle(el).position !== 'absolute') return;
    const margin = 12;
    const vw = document.documentElement.clientWidth;
    const left = wrap.current.getBoundingClientRect().left; // the popover hangs from left: 0
    const right = left + el.offsetWidth;
    let dx = 0;
    if (right > vw - margin) dx = vw - margin - right;
    if (left + dx < margin) dx = margin - left;
    el.style.translate = dx ? `${Math.round(dx)}px 0` : '';
  }, [open]);

  // Escape closes; so does a click anywhere outside the field.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    const onDown = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  return (
    <div className="datefield" ref={wrap}>
      <button
        type="button"
        className={`datefield-btn${open ? ' open' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <i className="ri-calendar-line" />
        <span className={value ? '' : 'ph'}>{value ? label : placeholder}</span>
        <i className="ri-arrow-down-s-line chev" />
      </button>

      {open && (
        <div className="datefield-pop" ref={pop} role="dialog" aria-label={t('dfChooseDate')}>
          <Calendar
            value={value}
            min={min}
            onPick={(v) => {
              onPick(v);
              setOpen(false);
            }}
          />
        </div>
      )}
    </div>
  );
}

/* ------------------------------ time columns ----------------------------- */

const HOURS12 = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const MINUTES = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

/** "HH:mm" (24h) -> the three wheels. */
const split = (hhmm: string) => {
  const [h, m] = hhmm.split(':').map(Number);
  return { h12: ((h + 11) % 12) + 1, minute: m, pm: h >= 12 };
};

/** the three wheels -> "HH:mm" (24h), which is what the value contract wants */
const join = (h12: number, minute: number, pm: boolean) =>
  `${pad((h12 % 12) + (pm ? 12 : 0))}:${pad(minute)}`;

function TimeColumns({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { t, locale } = useT();
  const { h12, minute, pm } = split(value || '09:00');
  const cols = useRef<HTMLDivElement>(null);

  // centre each wheel on its current selection when the panel opens
  useEffect(() => {
    cols.current?.querySelectorAll('.time-opt.sel').forEach((el) => {
      (el as HTMLElement).scrollIntoView({ block: 'center' });
    });
  }, []);

  return (
    <div className="time-cols" ref={cols}>
      <div className="time-col">
        <span className="eyebrow">{t('dfHour')}</span>
        <div className="time-scroll">
          {HOURS12.map((h) => (
            <button
              type="button"
              key={h}
              className={`time-opt${h === h12 ? ' sel' : ''}`}
              onClick={() => onChange(join(h, minute, pm))}
            >
              {pad(h)}
            </button>
          ))}
        </div>
      </div>

      <div className="time-col">
        <span className="eyebrow">{t('dfMinute')}</span>
        <div className="time-scroll">
          {MINUTES.map((m) => (
            <button
              type="button"
              key={m}
              className={`time-opt${m === minute ? ' sel' : ''}`}
              onClick={() => onChange(join(h12, m, pm))}
            >
              {pad(m)}
            </button>
          ))}
        </div>
      </div>

      <div className="time-col">
        <span className="eyebrow">{t('dfMeridiem')}</span>
        <div className="time-scroll">
          {[false, true].map((isPm) => (
            <button
              type="button"
              key={String(isPm)}
              className={`time-opt${isPm === pm ? ' sel' : ''}`}
              onClick={() => onChange(join(h12, minute, isPm))}
            >
              {meridiem(isPm, locale)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------- fields --------------------------------- */

interface DateProps {
  /** "YYYY-MM-DD" — empty string means unset. */
  value: string;
  onChange: (value: string) => void;
  /** Earliest selectable day, "YYYY-MM-DD". */
  min?: string;
  placeholder?: string;
}

export function DateField({ value, onChange, min, placeholder }: DateProps) {
  const { t, locale } = useT();
  return (
    <Popover
      value={value}
      min={min}
      label={value ? pretty(value, locale) : ''}
      placeholder={placeholder ?? t('dfSelectDate')}
      onPick={onChange}
    />
  );
}

interface DateTimeProps {
  /** "YYYY-MM-DDTHH:mm" — empty string means unset. */
  value: string;
  onChange: (value: string) => void;
  /** Earliest selectable moment, "YYYY-MM-DDTHH:mm". Only its day part gates the calendar. */
  min?: string;
}

/**
 * Date + time, both ours — the native time input's spinner is OS chrome and
 * can't be themed either.
 *
 * The two buttons sit in a row and share ONE panel that expands beneath them.
 * A floating popover would be clipped by a modal's scroll, and the time field
 * is far too narrow to host three wheels inside itself.
 */
export function DateTimeField({ value, onChange, min }: DateTimeProps) {
  const { t, locale } = useT();
  const [open, setOpen] = useState<'date' | 'time' | null>(null);
  const wrap = useRef<HTMLDivElement>(null);

  const [datePart, timePart] = value ? value.split('T') : ['', ''];

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(null);
    const onDown = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(null);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  /** A time chosen before a date anchors to the earliest day we're allowed. */
  const anchorDay = () => datePart || min?.slice(0, 10) || localToday();

  return (
    <div className="datetime" ref={wrap}>
      <div className="datetime-row">
        <button
          type="button"
          className={`datefield-btn${open === 'date' ? ' open' : ''}`}
          onClick={() => setOpen((o) => (o === 'date' ? null : 'date'))}
          aria-expanded={open === 'date'}
        >
          <i className="ri-calendar-line" />
          <span className={datePart ? '' : 'ph'}>
            {datePart ? pretty(datePart, locale) : t('dfSelectDate')}
          </span>
          <i className="ri-arrow-down-s-line chev" />
        </button>

        <button
          type="button"
          className={`datefield-btn time-btn${open === 'time' ? ' open' : ''}`}
          onClick={() => setOpen((o) => (o === 'time' ? null : 'time'))}
          aria-expanded={open === 'time'}
          aria-label={t('time')}
        >
          <i className="ri-time-line" />
          <span className={timePart ? '' : 'ph'}>
            {timePart ? prettyTime(timePart, locale) : '--:--'}
          </span>
        </button>
      </div>

      {open && (
        <div
          className="dt-panel"
          role="dialog"
          aria-label={open === 'date' ? t('dfChooseDate') : t('dfChooseTime')}
        >
          {open === 'date' ? (
            <Calendar
              value={datePart}
              min={min ? min.slice(0, 10) : undefined}
              // keep the time already chosen; a fresh pick defaults to 09:00
              onPick={(d) => {
                onChange(`${d}T${timePart || '09:00'}`);
                setOpen('time'); // date chosen -> the time is what's still missing
              }}
            />
          ) : (
            <>
              <TimeColumns
                value={timePart || '09:00'}
                onChange={(t) => onChange(`${anchorDay()}T${t}`)}
              />
              <div className="cal-foot">
                <button type="button" className="btn ghost sm" onClick={() => setOpen(null)}>
                  Done
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
